import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Clock } from 'lucide-react';
import { Callout } from '../../components/workbook/Callout';

const decidePages = [
  { number: 1, title: 'Commitment Check', description: 'Decide to prioritize (or not)', duration: '5 min' },
  { number: 2, title: 'Priority Inventory', description: 'List every active commitment', duration: '10 min' },
  { number: 3, title: 'Filter & Focus', description: 'Apply criteria to narrow scope', duration: '8 min' },
  { number: 4, title: 'Criteria Definition', description: 'Define what matters most', duration: '12 min' },
  { number: 5, title: 'Decision Play', description: 'Practice making priority decisions', duration: '15 min' },
  { number: 6, title: 'Commitment Statement', description: 'Articulate your prioritization approach', duration: '8 min' },
  { number: 7, title: 'Stakeholder Alignment', description: 'Align with key stakeholders', duration: '10 min' },
  { number: 8, title: 'Process Setup', description: 'Establish prioritization rhythms', duration: '12 min' },
];

export default function DecidePhase() {
  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      {/* Header */}
      <div className="bg-[var(--green-dim)] border-b border-[var(--green)]">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-[var(--green)] rounded-full flex items-center justify-center text-white text-xl font-bold">
              D
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">Decide</h1>
              <p className="text-[var(--green)] dark:text-[var(--text-ter)]">Commit to Prioritizing Intentionally</p>
            </div>
          </div>
          <p className="text-[var(--green)] dark:text-[var(--text-ter)] max-w-3xl">
            The foundation phase where you commit to prioritizing intentionally. 
            This isn't about doing more—it's about choosing what matters most 
            and aligning your actions with your intentions.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Phase Overview */}
            <div className="bg-[var(--surface)] p-6">
              <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
                Phase Overview
              </h2>
              <div className="space-y-4">
                <p className="text-[var(--text-sec)]">
                  Before you can prioritize effectively, you need to decide to prioritize. 
                  This phase helps you:
                </p>
                <ul className="space-y-2 text-[var(--text-sec)]">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[var(--green)] mt-0.5 flex-shrink-0" />
                    <span>Commit to intentional prioritization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[var(--green)] mt-0.5 flex-shrink-0" />
                    <span>Inventory all active commitments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[var(--green)] mt-0.5 flex-shrink-0" />
                    <span>Define clear criteria for decisions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[var(--green)] mt-0.5 flex-shrink-0" />
                    <span>Establish prioritization rhythms</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Pages List */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">
                Pages in This Phase
              </h2>
              {decidePages.map((page) => (
                <Link
                  key={page.number}
                  to={`/elevation/workbook/decide/page-${page.number}`}
                  className="block p-4 border border-[var(--rule)] hover:border-[var(--green)] hover:bg-[var(--green-dim)] transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 bg-[var(--green)] text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {page.number}
                      </div>
                      <div>
                        <h3 className="font-semibold text-[var(--text-pri)] group-hover:text-[var(--green)] dark:group-hover:text-[var(--text-ter)]">
                          {page.title}
                        </h3>
                        <p className="text-sm text-[var(--text-sec)]">
                          {page.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
                      <Clock className="w-4 h-4" />
                      {page.duration}
                      <ArrowRight className="w-4 h-4 group-hover:text-[var(--green)]" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Key Concepts */}
            <Callout type="why" phase="decide">
              <div className="space-y-2">
                <p className="font-semibold">Key Concepts:</p>
                <ul className="space-y-1 text-sm">
                  <li>• <strong>Commitment:</strong> Deciding to prioritize is a choice, not a given</li>
                  <li>• <strong>Inventory:</strong> You can't prioritize what you can't see</li>
                  <li>• <strong>Criteria:</strong> Clear standards enable consistent decisions</li>
                  <li>• <strong>Rhythm:</strong> Regular prioritization prevents drift</li>
                </ul>
              </div>
            </Callout>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Start */}
            <div className="bg-[var(--green-dim)] border border-[var(--green)] p-4">
              <h3 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-3">
                Quick Start
              </h3>
              <div className="space-y-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                <p>1. Start with Commitment Check</p>
                <p>2. Complete Priority Inventory</p>
                <p>3. Define your criteria</p>
                <p>4. Set up regular rhythms</p>
              </div>
            </div>

            {/* Timing Guide */}
            <div className="bg-[var(--surface)] border border-[var(--rule)] p-4">
              <h3 className="font-semibold text-[var(--text-pri)] mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Timing Guide
              </h3>
              <div className="space-y-2 text-sm text-[var(--text-sec)]">
                <div className="flex justify-between">
                  <span>Solo work:</span>
                  <span className="font-medium">~80 min</span>
                </div>
                <div className="flex justify-between">
                  <span>Team session:</span>
                  <span className="font-medium">~120 min</span>
                </div>
                <div className="flex justify-between">
                  <span>Coach workshop:</span>
                  <span className="font-medium">~90 min</span>
                </div>
              </div>
            </div>

            {/* Success Criteria */}
            <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
              <h3 className="font-semibold text-[var(--text-sec)] mb-3">
                Success Criteria
              </h3>
              <div className="space-y-2 text-sm text-[var(--text-sec)]">
                <p>✓ Clear commitment to prioritize</p>
                <p>✓ Complete inventory of commitments</p>
                <p>✓ Defined decision criteria</p>
                <p>✓ Established prioritization rhythm</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between">
          <Link
            to="/elevation/workbook/home"
            className="px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
          >
            ← Back to Overview
          </Link>
          <Link
            to="/elevation/workbook/decide/page-1"
            className="inline-flex items-center gap-2 bg-[var(--green)] text-white px-6 py-3 font-semibold hover:bg-[var(--green)] transition-colors"
          >
            Start Decide Phase
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}






